import React, { useContext, useEffect, useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
    TextField, 
    Button, 
    Typography, 
    Alert, 
    Box,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { StateContext } from '../contexts/StateContext';
import { useKeycloak } from '../contexts/KeycloakContext';

// Domain to IdP password reset URL mapping
// Default: Okta handles all domains except companyABC.tst
// NOTE: Keys must be lowercase because domain is extracted with .toLowerCase()
const DOMAIN_PASSWORD_RESET_MAP = {
    'companyabc.tst': 'http://10.48.49.151:8080/realms/test-realm/login-actions/reset-credentials'
    // All other domains default to Okta
};

const OKTA_PASSWORD_RESET_URL = 'https://id-qa.eaton.com/signin/forgot-password';

export const ChangePasswordScreeen = () => {
    const { showChangePasswordDailog, setShowChangePasswordDailog } = useContext(StateContext);
    const { keycloak } = useKeycloak();
    
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Pre-fill email from authenticated user's token when dialog opens
        if (showChangePasswordDailog && keycloak?.tokenParsed?.email) {
            setEmail(keycloak.tokenParsed.email);
        }
    }, [showChangePasswordDailog, keycloak]);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleClose = () => {
        setShowChangePasswordDailog(false);
        setEmail('');
        setError('');
        setLoading(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Validate email format
            if (!validateEmail(email)) {
                setError('Please enter a valid email address');
                setLoading(false);
                return;
            }

            // Extract domain from email
            const domain = email.toLowerCase().split('@')[1];

            // Map domain to IdP password reset URL (default to Okta for all domains except companyABC.tst)
            const resetUrl = DOMAIN_PASSWORD_RESET_MAP[domain] || OKTA_PASSWORD_RESET_URL;

            // Redirect to IdP's password reset page
            // Keycloak uses 'username' parameter, Okta uses 'email' parameter
            const paramName = domain === 'companyabc.tst' ? 'username' : 'email';
            window.location.href = `${resetUrl}?${paramName}=${encodeURIComponent(email)}`;
        } catch (err) {
            console.error('Password reset error:', err);
            setError('An error occurred. Please try again.');
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && email) {
            handleSubmit(e);
        }
    };

    return (
        <Dialog
            open={showChangePasswordDailog}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: 400,
                    borderRadius: 2
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" component="span">
                    Change Password
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                    Enter your email address and we'll redirect you to your identity provider to reset your password
                </Typography>

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        id="email"
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="user@eaton.com"
                        disabled={loading}
                        autoFocus
                        variant="outlined"
                        margin="normal"
                        required
                    />

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Password Reset Authentication:
                        </Typography>
                        <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
                            <Typography component="li" variant="body2" color="textSecondary">
                                @companyABC.tst → Test Keycloak
                            </Typography>
                            <Typography component="li" variant="body2" color="textSecondary">
                                All other domains → Okta
                            </Typography>
                        </Box>
                    </Box>
                </form>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button 
                    onClick={handleClose} 
                    color="secondary"
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={loading || !email}
                    sx={{ minWidth: 120 }}
                >
                    {loading ? 'Redirecting...' : 'Continue'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
